import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { CreateUpdateBooking, ResourceFiltersTypes } from "../../createOrderTypes"
import { WeeklyResourceBookingCalendar } from "./resourceBookingCalendars/WeeklyResourceBookingCalendar"
import { TimelineCalendarEvent, TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { ResourceBookingCalendar } from "./resourceBookingCalendars/ResourceBookingCalendar"
import { BookingViewMenu, ViewType } from "./resourceBookingCalendars/BookingViewMenu"
import { ResourceFilters } from "./resourceFilters/ResourceFilters"
import {
    saveServiceCenterFilter,
    SERVICE_CENTER_FILTER_IDENTIFIER,
} from "./resourceFilters/timelineLocationFilterStorage"
import {
    buildSittingsFilter,
    countMeetingFit,
    createInitialFilters,
    SITTINGS_FILTER_IDENTIFIER,
} from "./resourceFilters/meetingFit"
import { saveMeetingFitPreference } from "./resourceFilters/timelineMeetingFitStorage"
import { useMeetingAvailability } from "./resourceFilters/useMeetingAvailability"
import { HiddenResourcesNotice } from "./resourceFilters/HiddenResourcesNotice"
import { MeetingFitBanner } from "./resourceFilters/MeetingFitBanner"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { ResourceBookingControls } from "./ResourceBookingControls"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { filterResources, toTimelineResourcesDependencies } from "./helpers"
import {
    ConfigQuery,
    CreateUpdateBookingResponse,
    fetchTimelineResources,
    TimelineResourcesResponse,
} from "../../createOrderApi"
import { OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ResourceBookingConfirmDialog } from "./ResourceBookingConfirmDialog"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { useConfirmBookingDialog } from "./ResourceBookingConfirmDialogContext"
import { TimelineContextProvider } from "./context/TimelineContextProvider"
import { ResourceDetailsProvider } from "./context/ResourceDetailsProvider"
import { ResourceLabelSharedProvider } from "./context/ResourceLabelContext"
import { useBookingAvailabilityGuard } from "./useBookingAvailabilityGuard"
import { useCreateOrderForm, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { ResourceBookingCalendarSkeleton } from "./ResourceBookingCalendarSkeleton"

const useStyles = makeStyles()((theme) => ({
    calendarWrapper: {
        position: "relative",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    headerWrap: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: "1rem",
        alignItems: "start",
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    button: {
        color: theme.palette.tailwind.slate[800],
        padding: ".5rem 1rem",
        flex: "none",
    },
}))

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) {
        return { calendarResources: [], groupImagesMap: new Map<string, string>() }
    }

    const groupImagesMap = new Map<string, string>()
    const calendarResources: TimelineCalendarResource[] = []

    serviceCentersData.forEach((group) => {
        if (group.floorPlan?.imageUrl) {
            groupImagesMap.set(group.name, group.floorPlan.imageUrl)
        }

        group.resources.forEach((resource) => {
            calendarResources.push({
                id: String(resource.id),
                uid: resource.uid,
                location: group.name,
                locationId: `${String(group.id)}_${String(resource.id)}`,
                serviceCenterUid: group.uid,
                title: resource.displayName || resource.name,
                seats: resource.room?.seats,
                floor: resource.room?.floorDetails?.value,
                avEquipments: resource.room?.avEquipments || [],
                buffer: resource.buffer,
                tableSetups: resource.room?.tableSetups,
                outOfService: resource.outOfService,
                image: resource.image,
                meetingUsage: resource.room?.meetingUsage,
                // hasMessagingEnabled: !!group.messaging,
                type: resource.type,
                accessControl: resource.accessControl,
            })
        })
    })

    return { calendarResources, groupImagesMap }
}

type ResourceBookingProps = Readonly<{
    handleOpenSuccessDialog: (
        orderDetails: CreateUpdateBookingResponse,
        order: CreateUpdateBooking,
        isBookingUpdate: boolean,
    ) => void
    handleClearResourceIds: () => void
    orderDetails?: MeetingServiceOrder
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ResourceBooking({
    handleOpenSuccessDialog,
    handleClearResourceIds,
    orderDetails,
    orderFormStatusRef,
}: ResourceBookingProps) {
    const { user, orderContext, setOrderContext, flowConfig } = useCreateOrderForm()
    const { switchToOrder } = useCreateOrderFormSetOrder()
    const { confirmBookingDialog, setConfirmBookingDialog } = useConfirmBookingDialog()
    const { orderId, mode } = orderContext
    const isOutlookFlow = !!flowConfig?.flow

    // Recurring bookings (an Outlook series) behave like "Reserve period": the user selects
    // resources via checkboxes instead of drawing on the timeline. The period is dictated by the
    // series, so this mode is forced on and cannot be switched off.
    const isRecurringBooking = Boolean(flowConfig?.order?.meetingDetails?.recurrence?.pattern)

    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { goToMeeting } = useRefreshCalendar()
    const {
        draftEvent: event,
        fullResources,
        setDraftEvent,
        syncDraftToFormik,
        initialEventStateId,
        handleCancelClick,
    } = useResourceBookingDraft()

    // Older add-ins send only an organizer-inclusive headcount, where 1 means nobody was invited.
    // Subtracting one is wrong: the organizer is left out whenever Outlook already listed them.
    const legacyHeadcount =
        flowConfig?.order?.meetingDetails?.outlookAttendeeCount ?? flowConfig?.order?.deliveryDetails?.sittings ?? 0
    const outlookGuestCount = flowConfig?.outlookInviteeCount ?? (legacyHeadcount > 1 ? legacyHeadcount : 0)

    const [selectedView, setSelectedView] = useState(ViewType.TIMELINE)
    const [filters, setFilters] = useState<ResourceFiltersTypes>(() =>
        createInitialFilters({ userId: user.userId, isOutlookFlow, guestCount: outlookGuestCount, isRecurringBooking }),
    )
    // Bumped whenever the filters are changed programmatically, to remount the chips with them.
    const [chipsSeed, setChipsSeed] = useState(0)
    const [toolbarStartSlot, setToolbarStartSlot] = useState<HTMLElement | null>(null)
    const hasReconciledLocationsRef = useRef(false)

    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const {
        data: { calendarResources, groupImagesMap } = {
            calendarResources: [],
            groupImagesMap: new Map<string, string>(),
        },
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: toTimelineResourcesDependencies(user.userId, filters.filters),

        select: transformData,
    })

    const activeResourceIdsKey = selectedView === ViewType.TIMELINE ? (event?.resourceIds?.join("|") ?? "") : ""
    const activeResourceIdsForFiltering = useMemo<readonly string[] | null>(
        () => (activeResourceIdsKey ? activeResourceIdsKey.split("|") : null),
        [activeResourceIdsKey],
    )

    const { meetingWindow, busyResourceIds, meetingBusyResourceIds, isAwaitingAvailability } = useMeetingAvailability({
        userId: user.userId,
        isOutlookFlow,
        calendarResources,
        filters,
        selectedView,
        draftEvent: event,
    })

    const visibleResources = useMemo(
        () => filterResources(calendarResources, filters.filters, searchRegex, null, busyResourceIds),
        [calendarResources, filters.filters, searchRegex, busyResourceIds],
    )
    // pin only the hidden ones: a new list makes FullCalendar rebuild every row
    const hiddenActiveResourceIdsKey = useMemo(() => {
        if (!activeResourceIdsForFiltering) return ""

        const visibleIds = new Set(visibleResources.map((resource) => resource.id))
        return activeResourceIdsForFiltering.filter((id) => !visibleIds.has(id)).join("|")
    }, [activeResourceIdsForFiltering, visibleResources])

    const filteredResources = useMemo(
        () =>
            hiddenActiveResourceIdsKey
                ? filterResources(
                      calendarResources,
                      filters.filters,
                      searchRegex,
                      hiddenActiveResourceIdsKey.split("|"),
                      busyResourceIds,
                  )
                : visibleResources,
        [
            hiddenActiveResourceIdsKey,
            visibleResources,
            calendarResources,
            filters.filters,
            searchRegex,
            busyResourceIds,
        ],
    )

    const meetingFitCounts = useMemo(
        () =>
            isOutlookFlow
                ? countMeetingFit({
                      calendarResources,
                      filters: filters.filters,
                      searchRegex,
                      activeResourceIds: activeResourceIdsForFiltering,
                      busyResourceIds: meetingBusyResourceIds,
                      guestCount: outlookGuestCount,
                  })
                : undefined,
        [
            isOutlookFlow,
            calendarResources,
            filters.filters,
            searchRegex,
            activeResourceIdsForFiltering,
            meetingBusyResourceIds,
            outlookGuestCount,
        ],
    )
    const hiddenCount = meetingFitCounts ? meetingFitCounts.allCount - filteredResources.length : 0

    const handleToggleMeetingFit = useCallback(
        (next: boolean) => {
            setFilters((prev) => {
                const withoutSittings = prev.filters.filter((f) => f.identifier !== SITTINGS_FILTER_IDENTIFIER)

                return {
                    ...prev,
                    filters: next ? [...withoutSittings, ...buildSittingsFilter(outlookGuestCount)] : withoutSittings,
                    onlyAvailableForMeeting: next,
                }
            })
            setChipsSeed((seed) => seed + 1)
            saveMeetingFitPreference(user.userId, next)
        },
        [outlookGuestCount, user.userId],
    )

    const handleShowAll = useCallback(() => handleToggleMeetingFit(false), [handleToggleMeetingFit])

    const outlookMeetingStartRaw = flowConfig?.order?.meetingDetails?.startDateTime
    const outlookMeetingStart = useMemo(() => {
        if (!outlookMeetingStartRaw) return undefined

        const start = dateTimeFactory.fromApi(outlookMeetingStartRaw)
        return start.isValid ? start : undefined
    }, [outlookMeetingStartRaw, dateTimeFactory])

    const handleGoToMeetingTime = useCallback(() => {
        if (outlookMeetingStart) goToMeeting(outlookMeetingStart)
    }, [outlookMeetingStart, goToMeeting])

    const showMeetingFitBanner =
        isOutlookFlow && selectedView === ViewType.TIMELINE && (!!meetingWindow || outlookGuestCount > 0)
    // Either half counts as on, so editing or dismissing the seats chip by hand does not desync the banner.
    const isMeetingFitOn =
        !!filters.onlyAvailableForMeeting || filters.filters.some((f) => f.identifier === SITTINGS_FILTER_IDENTIFIER)

    // Only the locations on screen: resources of the active booking survive `filterResources`, so
    // their events (and the overlap index built from them) are still fetched.
    const serviceCenterIdsForEventsKey = useMemo(
        () => [...new Set(filteredResources.map((r) => r.locationId.split("_")[0]))].sort().join("|"),
        [filteredResources],
    )
    // Keyed by the id set, so picking a resource or typing in search does not recreate the event sources.
    const allServiceCenterIdsForEvents = useMemo(
        () => (serviceCenterIdsForEventsKey ? serviceCenterIdsForEventsKey.split("|") : []),
        [serviceCenterIdsForEventsKey],
    )

    useEffect(() => {
        const values = filters.filters.find((f) => f.identifier === SERVICE_CENTER_FILTER_IDENTIFIER)?.values ?? []
        saveServiceCenterFilter(user.userId, values)
    }, [filters.filters, user.userId])

    // A restored location that no longer exists would leave the timeline empty with no way back
    // other than the chip, so drop the whole filter once the resources prove none of it is valid.
    useEffect(() => {
        if (hasReconciledLocationsRef.current || !calendarResources.length) return
        hasReconciledLocationsRef.current = true

        const savedValues = filters.filters.find((f) => f.identifier === SERVICE_CENTER_FILTER_IDENTIFIER)?.values
        if (!savedValues?.length) return

        const availableIds = new Set(calendarResources.map((r) => r.locationId.split("_")[0]))
        if (savedValues.some((value) => availableIds.has(String(value.value)))) return

        setFilters((prev) => ({
            ...prev,
            filters: prev.filters.filter((f) => f.identifier !== SERVICE_CENTER_FILTER_IDENTIFIER),
        }))
        setChipsSeed((seed) => seed + 1)
    }, [calendarResources, filters.filters])

    // A draft seeded with bare resource ids (a prefilled create) gets the resources' details from the
    // loaded list; an id the user cannot book is dropped, like a resource deselected by hand.
    useEffect(() => {
        if (isLoading || !event) return

        const knownIds = new Set(fullResources.map((resource) => String(resource.id)))
        if (event.resourceIds.every((resourceId) => knownIds.has(resourceId))) return

        const resolved = calendarResources
            .filter((resource) => event.resourceIds.includes(resource.id) && !knownIds.has(resource.id))
            .map((resource) => ({ ...resource, id: Number(resource.id) }))
        const resolvedIds = new Set(resolved.map((resource) => String(resource.id)))
        const resourceIds = event.resourceIds.filter(
            (resourceId) => knownIds.has(resourceId) || resolvedIds.has(resourceId),
        )

        if (resourceIds.length) {
            setDraftEvent({ ...event, resourceIds }, [...fullResources, ...resolved])
            return
        }

        // The saved resource type filter only loads those types, so the prefilled resource may just be filtered out.
        if (filters.filters.some((f) => f.identifier === "resourceTypeIds")) {
            setFilters((prev) => ({
                ...prev,
                filters: prev.filters.filter((f) => f.identifier !== "resourceTypeIds"),
            }))
            setChipsSeed((seed) => seed + 1)
            return
        }

        setDraftEvent(null)
    }, [isLoading, event, fullResources, calendarResources, filters.filters, setDraftEvent])

    const { ensureDraftStillAvailable, isChecking } = useBookingAvailabilityGuard({ resources: calendarResources })

    const handleBookClick = useCallback(async () => {
        if (!(await ensureDraftStillAvailable(event))) return

        syncDraftToFormik()
        setConfirmBookingDialog(true)
    }, [ensureDraftStillAvailable, event, syncDraftToFormik, setConfirmBookingDialog])

    const handleOpenBookDialog = useCallback(
        async (draft: TimelineCalendarEvent) => {
            if (!(await ensureDraftStillAvailable(draft))) return

            setConfirmBookingDialog(true)
        },
        [ensureDraftStillAvailable, setConfirmBookingDialog],
    )

    const handleChangeBookingView = (view: ViewType) => {
        if (selectedView === ViewType.AVAILABLE_TIME && event) {
            setDraftEvent({ ...event, timeRange: undefined })
        }
        setSelectedView(view)
        handleCancelClick()
    }

    const handleChangeOrderContext = useCallback((id: string) => switchToOrder(+id), [switchToOrder])

    return (
        <Box className={classes.calendarWrapper}>
            <Box className={classes.headerWrap}>
                <ResourceFilters
                    filters={filters}
                    userId={user.userId}
                    setFilters={setFilters}
                    selectedView={selectedView}
                    chipsSeed={chipsSeed}
                    isTimeline
                    isSpecialFlow={isOutlookFlow}
                    isRecurringBooking={isRecurringBooking}
                />

                <BookingViewMenu selectedView={selectedView} onChangeView={handleChangeBookingView} />
            </Box>

            {selectedView === ViewType.AVAILABLE_TIME && !isAwaitingAvailability && hiddenCount > 0 && (
                <HiddenResourcesNotice count={hiddenCount} onShowAll={handleShowAll} />
            )}

            {!isLoading && !isAwaitingAvailability ? (
                <ResourceDetailsProvider>
                    {selectedView === ViewType.TIMELINE && (
                        <TimelineContextProvider
                            timelineResources={filteredResources}
                            allServiceCenterIdsForEvents={allServiceCenterIdsForEvents}
                            userId={user.userId}
                            localEventUpdateId={initialEventStateId || orderId}
                            isBookPeriodMode={!!filters.isBookedPeriod}
                            isOutlookFlow={isOutlookFlow}
                            searchRegex={searchRegex}
                        >
                            <ResourceLabelSharedProvider>
                                <ResourceBookingCalendar
                                    groupImagesMap={groupImagesMap}
                                    handleChangeOrderContext={handleChangeOrderContext}
                                    onToolbarStartSlotChange={setToolbarStartSlot}
                                />
                            </ResourceLabelSharedProvider>
                        </TimelineContextProvider>
                    )}
                    {selectedView === ViewType.AVAILABLE_TIME && (
                        <WeeklyResourceBookingCalendar
                            timelineResources={filteredResources}
                            searchRegex={searchRegex}
                            groupImagesMap={groupImagesMap}
                            handleBookClick={handleOpenBookDialog}
                        />
                    )}
                </ResourceDetailsProvider>
            ) : (
                <ResourceBookingCalendarSkeleton viewType={selectedView} />
            )}
            {selectedView === ViewType.TIMELINE && (
                <ResourceBookingControls
                    onBook={handleBookClick}
                    isLoadingButton={confirmBookingDialog || isChecking}
                    flow={flowConfig?.flow}
                    orderFormStatusRef={orderFormStatusRef}
                />
            )}

            {showMeetingFitBanner &&
                meetingFitCounts &&
                toolbarStartSlot &&
                createPortal(
                    <MeetingFitBanner
                        meetingWindow={meetingWindow}
                        guestCount={outlookGuestCount}
                        fitsCount={isMeetingFitOn ? filteredResources.length : meetingFitCounts.fitsCount}
                        allCount={meetingFitCounts.allCount}
                        isOn={isMeetingFitOn}
                        onToggle={handleToggleMeetingFit}
                        onGoToTime={outlookMeetingStart ? handleGoToMeetingTime : undefined}
                    />,
                    toolbarStartSlot,
                )}

            {confirmBookingDialog && event && (
                <ResourceBookingConfirmDialog
                    open={confirmBookingDialog}
                    user={user}
                    orderId={orderId}
                    initialEventStateId={initialEventStateId}
                    orderFormStatusRef={orderFormStatusRef}
                    orderDetails={orderDetails}
                    mode={mode}
                    setOrderContext={setOrderContext}
                    handleOpenSuccessDialog={handleOpenSuccessDialog}
                    handleClearResourceIds={handleClearResourceIds}
                />
            )}
        </Box>
    )
}
